import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Flex, Text, Button, Badge, Icon, SimpleGrid, Skeleton, useToast,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, IconButton, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  FormControl, FormLabel, Input, Select, useDisclosure, VStack, HStack,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Alert, AlertIcon, Radio, RadioGroup, Stack
} from '@chakra-ui/react';
import { Card, Title } from "@tremor/react";
import {
  Plus, Pencil, Trash2, FolderTree, RefreshCw, Layers, ChevronRight, Tag
} from 'lucide-react';
import ScrollToTop from "../components/ScrollToTop";
import { getDreCategorias, createDreCategoria, updateDreCategoria, deleteDreCategoria } from "../services/api";

const badgeColors = ['blue', 'green', 'purple', 'cyan', 'orange', 'teal', 'pink', 'red'];
const getBadgeColor = (str) => {
  let hash = 0;
  for (let i = 0; i < (str || '').length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return badgeColors[Math.abs(hash) % badgeColors.length];
};

const DRECategorias = () => {
  const toast = useToast();
  const dataFetchedRef = useRef(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias] = useState({ data: {}, lista: [] });
  const [saving, setSaving] = useState(false);

  // Form
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ grupo: '', categoria: '', subcategoria: '', item: '', tipo: 'despesa' });

  // Grupos e categorias existentes para autocomplete
  const gruposExistentes = [...new Set((categorias.lista || []).map(c => c.grupo).filter(Boolean))];
  const categoriasExistentes = [...new Set((categorias.lista || []).map(c => c.categoria).filter(Boolean))];

  const resetForm = () => {
    setEditId(null);
    setForm({ grupo: '', categoria: '', subcategoria: '', item: '', tipo: 'despesa' });
  };

  const fetchCategorias = async () => {
    setLoading(true);
    const res = await getDreCategorias();
    if (res) setCategorias(res);
    else toast({ title: 'Erro ao carregar categorias', status: 'error', duration: 3000 });
    setLoading(false);
  };

  useEffect(() => {
    if (!dataFetchedRef.current) {
      fetchCategorias();
      dataFetchedRef.current = true;
    }
  }, []);

  const handleOpenNew = () => { resetForm(); onOpen(); };

  const handleOpenEdit = (cat) => {
    setEditId(cat.id);
    setForm({
      grupo: cat.grupo,
      categoria: cat.categoria,
      subcategoria: cat.subcategoria,
      item: cat.item || '',
      tipo: cat.tipo || 'despesa',
    });
    onOpen();
  };

  const handleSave = async () => {
    if (!form.grupo || !form.categoria || !form.subcategoria) {
      toast({ title: 'Preencha todos os campos', status: 'warning', duration: 3000 });
      return;
    }

    setSaving(true);
    const payload = {
      grupo: form.grupo.trim(),
      categoria: form.categoria.trim(),
      subcategoria: form.subcategoria.trim(),
      item: form.item.trim(),
      tipo: form.tipo,
    };

    let res;
    if (editId) res = await updateDreCategoria(editId, payload);
    else res = await createDreCategoria(payload);

    if (res && (res.status === 200 || res.status === 201)) {
      toast({
        title: editId ? 'Categoria atualizada com sucesso' : 'Categoria criada com sucesso',
        status: 'success', duration: 3000
      });
      onClose();
      resetForm();
      fetchCategorias();
    } else if (res && res.status === 409) {
      toast({ title: 'Essa combinação já existe', status: 'warning', duration: 3000 });
    } else {
      const msg = res?.data?.message || 'Erro ao salvar categoria';
      toast({ title: msg, status: 'error', duration: 3000 });
    }
    setSaving(false);
  };

  const handleDelete = async (cat) => {
    const alvo = cat.item ? `o item "${cat.item}"` : `a subcategoria "${cat.subcategoria}"`;
    if (!window.confirm(`Excluir ${alvo} de ${cat.grupo} > ${cat.categoria} > ${cat.subcategoria}?`)) return;

    const res = await deleteDreCategoria(cat.id);
    if (res && res.status === 200) {
      toast({ title: 'Categoria excluída com sucesso', status: 'success', duration: 3000 });
      fetchCategorias();
    } else if (res && res.status === 422) {
      toast({
        title: res.data?.message || 'Não é possível excluir: existem custos vinculados',
        status: 'warning', duration: 5000
      });
    } else {
      toast({ title: 'Erro ao excluir categoria', status: 'error', duration: 3000 });
    }
  };

  // ---- Dados da árvore (nova estrutura: grupo → { tipo, categorias: { cat: [subs] } }) ----
  const tree = categorias?.data || {};
  const lista = categorias?.lista || [];
  const totalGrupos = Object.keys(tree).length;
  const totalCategorias = Object.values(tree).reduce((s, g) => s + Object.keys(g.categorias || {}).length, 0);
  const totalSubcategorias = new Set(lista.map(c => `${c.grupo}|${c.categoria}|${c.subcategoria}`)).size;
  const totalItens = lista.filter(c => c.item).length;

  // Agrupa as folhas de uma categoria pelo nome da subcategoria, preservando a
  // ordem que veio da API. Subcategoria sem item continua com uma folha só.
  const agruparPorSubcategoria = (folhas) => {
    const mapa = new Map();
    folhas.forEach((folha) => {
      if (!mapa.has(folha.subcategoria)) mapa.set(folha.subcategoria, []);
      mapa.get(folha.subcategoria).push(folha);
    });
    return [...mapa.entries()];
  };

  return (
    <Box className="min-h-screen bg-gray-50" pt="60px">
      <ScrollToTop />
      <Box maxW="1400px" mx="auto" px={{ base: 3, md: 6 }} py={6}>
        {/* Header */}
        <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">Categorias de Custo</Text>
            <Text fontSize="sm" color="gray.500">Hierarquia: Grupo › Categoria › Subcategoria › Item (opcional)</Text>
          </Box>
          <HStack spacing={2}>
            <Button size="sm" variant="outline" leftIcon={<RefreshCw size={14} />} onClick={fetchCategorias} isLoading={loading}>
              Atualizar
            </Button>
            <Button size="sm" colorScheme="blue" leftIcon={<Plus size={16} />} onClick={handleOpenNew}>
              Nova Categoria
            </Button>
          </HStack>
        </Flex>

        {/* Resumo */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
          <Card className="ring-0">
            <Flex align="center" gap={3}>
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg="blue.50">
                <Icon as={Layers} boxSize={5} color="blue.500" />
              </Flex>
              <Box>
                <Text fontSize="xs" color="gray.500">Grupos</Text>
                {loading ? <Skeleton h="24px" w="60px" /> : (
                  <Text fontSize="lg" fontWeight="bold" color="blue.600">{totalGrupos}</Text>
                )}
              </Box>
            </Flex>
          </Card>
          <Card className="ring-0">
            <Flex align="center" gap={3}>
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg="purple.50">
                <Icon as={FolderTree} boxSize={5} color="purple.500" />
              </Flex>
              <Box>
                <Text fontSize="xs" color="gray.500">Categorias</Text>
                {loading ? <Skeleton h="24px" w="60px" /> : (
                  <Text fontSize="lg" fontWeight="bold" color="purple.600">{totalCategorias}</Text>
                )}
              </Box>
            </Flex>
          </Card>
          <Card className="ring-0">
            <Flex align="center" gap={3}>
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg="teal.50">
                <Icon as={ChevronRight} boxSize={5} color="teal.500" />
              </Flex>
              <Box>
                <Text fontSize="xs" color="gray.500">Subcategorias</Text>
                {loading ? <Skeleton h="24px" w="60px" /> : (
                  <Text fontSize="lg" fontWeight="bold" color="teal.600">{totalSubcategorias}</Text>
                )}
              </Box>
            </Flex>
          </Card>
          <Card className="ring-0">
            <Flex align="center" gap={3}>
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg="orange.50">
                <Icon as={Tag} boxSize={5} color="orange.500" />
              </Flex>
              <Box>
                <Text fontSize="xs" color="gray.500">Itens</Text>
                {loading ? <Skeleton h="24px" w="60px" /> : (
                  <Text fontSize="lg" fontWeight="bold" color="orange.600">{totalItens}</Text>
                )}
              </Box>
            </Flex>
          </Card>
        </SimpleGrid>

        {/* Árvore hierárquica */}
        <Card className="ring-0 mb-6">
          <Title className="mb-4">Hierarquia de Categorias</Title>
          {loading ? (
            <VStack spacing={3}>{[...Array(5)].map((_, i) => <Skeleton key={i} h="40px" w="100%" />)}</VStack>
          ) : Object.keys(tree).length === 0 ? (
            <Text color="gray.400" textAlign="center" py={8}>Nenhuma categoria cadastrada</Text>
          ) : (
            <Accordion allowMultiple defaultIndex={Object.keys(tree).map((_, i) => i)}>
              {Object.entries(tree).map(([grupo, grupoData]) => {
                const grupoTipo = grupoData.tipo;
                const cats = grupoData.categorias || {};
                return (
                  <AccordionItem key={grupo} border="1px solid" borderColor="gray.100" borderRadius="lg" mb={3} overflow="hidden">
                    <AccordionButton bg="gray.50" _hover={{ bg: 'gray.100' }} py={3}>
                      <Flex flex={1} align="center" gap={2}>
                        <Badge colorScheme={getBadgeColor(grupo)} fontSize="xs" px={2} borderRadius="full">{grupo}</Badge>
                        <Text fontSize="sm" fontWeight="bold" color="gray.700">{grupo}</Text>
                        <Badge
                          colorScheme={grupoTipo === 'receita' ? 'green' : grupoTipo === 'misto' ? 'yellow' : 'red'}
                          fontSize="2xs" borderRadius="full" px={2} variant="subtle"
                        >
                          {grupoTipo === 'receita' ? 'Receita' : grupoTipo === 'misto' ? 'Misto' : 'Despesa'}
                        </Badge>
                        <Text fontSize="xs" color="gray.400" ml={2}>
                          {Object.keys(cats).length} categoria(s)
                        </Text>
                      </Flex>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={3} px={2}>
                      <Accordion allowMultiple>
                        {Object.entries(cats).map(([cat, catData]) => {
                          const catTipo = catData.tipo;
                          const subcats = Array.isArray(catData.subcategorias) ? catData.subcategorias : [];
                          return (
                            <AccordionItem key={cat} border="none" mb={1}>
                              <AccordionButton
                                bg="white" borderRadius="md" border="1px solid" borderColor="gray.100"
                                _hover={{ bg: 'blue.50', borderColor: 'blue.200' }} py={2} px={3}
                              >
                                <Flex flex={1} align="center" gap={2}>
                                  <Icon as={FolderTree} boxSize={3.5} color="gray.400" />
                                  <Text fontSize="sm" fontWeight="semibold" color="gray.700">{cat}</Text>
                                  <Badge
                                    colorScheme={catTipo === 'receita' ? 'green' : 'red'}
                                    fontSize="2xs" borderRadius="full" px={1.5} variant="outline"
                                  >
                                    {catTipo === 'receita' ? 'receita' : 'despesa'}
                                  </Badge>
                                  <Badge variant="subtle" colorScheme="gray" fontSize="2xs" borderRadius="full">
                                    {agruparPorSubcategoria(subcats).length} sub
                                  </Badge>
                                </Flex>
                                <AccordionIcon />
                              </AccordionButton>
                              <AccordionPanel pb={2} pl={6} pr={2}>
                                {agruparPorSubcategoria(subcats).map(([subNome, folhas]) => {
                                  const temItem = folhas.some(f => f.item);
                                  const linha = (folha, rotulo, recuo) => {
                                    const fullItem = lista.find(l => l.id === folha.id);
                                    return (
                                      <Flex
                                        key={folha.id} align="center" justify="space-between"
                                        py={2} px={3} pl={recuo} borderRadius="md"
                                        _hover={{ bg: 'gray.50' }} transition="background 0.15s"
                                        borderBottom="1px solid" borderColor="gray.50"
                                      >
                                        <Flex align="center" gap={2}>
                                          <Box w="6px" h="6px" borderRadius="full" bg={`${getBadgeColor(rotulo)}.400`} />
                                          <Text fontSize="sm" color={folha.item ? 'gray.700' : 'gray.600'}>{rotulo}</Text>
                                          <Badge
                                            colorScheme={folha.tipo === 'receita' ? 'green' : 'red'}
                                            fontSize="2xs" borderRadius="full" px={1.5} variant="outline"
                                          >
                                            {folha.tipo === 'receita' ? 'receita' : 'despesa'}
                                          </Badge>
                                        </Flex>
                                        <HStack spacing={1}>
                                          <Tooltip label="Editar">
                                            <IconButton
                                              icon={<Pencil size={13} />} size="xs" variant="ghost"
                                              colorScheme="blue"
                                              onClick={() => handleOpenEdit(fullItem || { id: folha.id, grupo, categoria: cat, subcategoria: folha.subcategoria, item: folha.item || '', tipo: folha.tipo })}
                                            />
                                          </Tooltip>
                                          <Tooltip label="Excluir">
                                            <IconButton
                                              icon={<Trash2 size={13} />} size="xs" variant="ghost"
                                              colorScheme="red"
                                              onClick={() => handleDelete({ id: folha.id, grupo, categoria: cat, subcategoria: folha.subcategoria, item: folha.item || '' })}
                                            />
                                          </Tooltip>
                                        </HStack>
                                      </Flex>
                                    );
                                  };

                                  // Sem quarto nível a subcategoria continua sendo uma linha só.
                                  if (!temItem) return folhas.map(f => linha(f, f.subcategoria, 3));

                                  return (
                                    <Box key={subNome}>
                                      <Flex align="center" gap={2} py={2} px={3}>
                                        <Icon as={ChevronRight} boxSize={3} color="gray.400" />
                                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">{subNome}</Text>
                                        <Badge variant="subtle" colorScheme="orange" fontSize="2xs" borderRadius="full">
                                          {folhas.filter(f => f.item).length} item(ns)
                                        </Badge>
                                      </Flex>
                                      {folhas.map(f => linha(f, f.item || 'Sem item', 8))}
                                    </Box>
                                  );
                                })}
                              </AccordionPanel>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </Card>

        {/* Tabela lista plana */}
        <Card className="ring-0">
          <Flex justify="space-between" align="center" mb={4}>
            <Title>Lista Completa</Title>
            <Badge colorScheme="gray" borderRadius="full" px={3}>{totalSubcategorias} itens</Badge>
          </Flex>
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr bg="gray.50">
                  <Th fontSize="xs">Grupo</Th>
                  <Th fontSize="xs">Categoria</Th>
                  <Th fontSize="xs">Subcategoria</Th>
                  <Th fontSize="xs">Item</Th>
                  <Th fontSize="xs">Tipo</Th>
                  <Th fontSize="xs" w="100px">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <Tr key={i}>
                      {[...Array(6)].map((_, j) => <Td key={j}><Skeleton h="16px" w="80px" /></Td>)}
                    </Tr>
                  ))
                ) : lista.length === 0 ? (
                  <Tr><Td colSpan={6}><Text color="gray.400" textAlign="center" py={6}>Nenhuma categoria cadastrada</Text></Td></Tr>
                ) : (
                  lista.map((c) => (
                    <Tr key={c.id} _hover={{ bg: 'gray.50' }}>
                      <Td>
                        <Badge colorScheme={getBadgeColor(c.grupo)} fontSize="xs" borderRadius="full" px={2}>
                          {c.grupo}
                        </Badge>
                      </Td>
                      <Td fontSize="sm" color="gray.700" fontWeight="medium">{c.categoria}</Td>
                      <Td fontSize="sm" color="gray.600">{c.subcategoria}</Td>
                      <Td fontSize="sm" color={c.item ? 'gray.700' : 'gray.300'}>{c.item || '-'}</Td>
                      <Td>
                        <Badge
                          colorScheme={c.tipo === 'receita' ? 'green' : 'red'}
                          fontSize="2xs" borderRadius="full" px={2}
                        >
                          {c.tipo === 'receita' ? 'Receita' : 'Despesa'}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <Tooltip label="Editar">
                            <IconButton
                              icon={<Pencil size={14} />} size="xs" variant="ghost"
                              colorScheme="blue" onClick={() => handleOpenEdit(c)}
                            />
                          </Tooltip>
                          <Tooltip label="Excluir">
                            <IconButton
                              icon={<Trash2 size={14} />} size="xs" variant="ghost"
                              colorScheme="red" onClick={() => handleDelete(c)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      {/* Modal Criar/Editar */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="lg">
            {editId ? 'Editar Categoria' : 'Nova Categoria'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Grupo</FormLabel>
                <Input
                  size="sm" placeholder="Ex: Custo, Despesas, Financeiro"
                  value={form.grupo}
                  onChange={(e) => setForm({ ...form, grupo: e.target.value })}
                  maxLength={50}
                  list="gruposList"
                />
                <datalist id="gruposList">
                  {gruposExistentes.map(g => <option key={g} value={g} />)}
                </datalist>
                <Text fontSize="2xs" color="gray.400" mt={1}>Nível mais alto (máx 50 caracteres)</Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Categoria</FormLabel>
                <Input
                  size="sm" placeholder="Ex: Vendas, Marketing, Folha de pagamento"
                  value={form.categoria}
                  onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                  maxLength={100}
                  list="categoriasList"
                />
                <datalist id="categoriasList">
                  {categoriasExistentes.map(c => <option key={c} value={c} />)}
                </datalist>
                <Text fontSize="2xs" color="gray.400" mt={1}>Nível intermediário (máx 100 caracteres)</Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Subcategoria</FormLabel>
                <Input
                  size="sm" placeholder="Ex: Publicidade, VPS, Pro-labore"
                  value={form.subcategoria}
                  onChange={(e) => setForm({ ...form, subcategoria: e.target.value })}
                  maxLength={100}
                />
                <Text fontSize="2xs" color="gray.400" mt={1}>Máx 100 caracteres</Text>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Item (opcional)</FormLabel>
                <Input
                  size="sm" placeholder="Ex: Meta ADS, Google ADS"
                  value={form.item}
                  onChange={(e) => setForm({ ...form, item: e.target.value })}
                  maxLength={100}
                />
                <Text fontSize="2xs" color="gray.400" mt={1}>
                  Quarto nível, para manter o mesmo nome do detalhe entre os meses. Em branco, o
                  lançamento fica direto na subcategoria.
                </Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Tipo</FormLabel>
                <RadioGroup value={form.tipo} onChange={(val) => setForm({ ...form, tipo: val })}>
                  <Stack direction="row" spacing={4}>
                    <Radio value="despesa" colorScheme="red" size="sm">
                      <Flex align="center" gap={1}>
                        <Text fontSize="sm">Despesa</Text>
                        <Text fontSize="2xs" color="gray.400">(subtrai do lucro)</Text>
                      </Flex>
                    </Radio>
                    <Radio value="receita" colorScheme="green" size="sm">
                      <Flex align="center" gap={1}>
                        <Text fontSize="sm">Receita</Text>
                        <Text fontSize="2xs" color="gray.400">(soma ao lucro)</Text>
                      </Flex>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {form.grupo && form.categoria && form.subcategoria && (
                <Alert status="info" borderRadius="md" fontSize="xs">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold" fontSize="xs">Preview:</Text>
                    <Flex align="center" gap={2}>
                      <Text fontSize="xs">
                        {form.grupo} › {form.categoria} › {form.subcategoria}
                        {form.item.trim() ? ` › ${form.item.trim()}` : ''}
                      </Text>
                      <Badge
                        colorScheme={form.tipo === 'receita' ? 'green' : 'red'}
                        fontSize="2xs" borderRadius="full" px={1.5}
                      >
                        {form.tipo}
                      </Badge>
                    </Flex>
                  </Box>
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
            <Button size="sm" colorScheme="blue" onClick={handleSave} isLoading={saving}>
              {editId ? 'Salvar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DRECategorias;
